<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div class="report-wrapper">
      <!-- Sticky Filter Header -->
      <div class="table-header-filters">
        <v-row dense class="align-center">
          <v-col cols="12" sm="6" md="3" lg="2">
            <v-menu v-model="reportFromMenu" offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  class="filter-select"
                  :value="formatDateFrom"
                  label="Od datuma"
                  dense outlined hide-details
                  prepend-inner-icon="mdi-calendar"
                  readonly v-bind="attrs" v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="dateFrom" no-title scrollable @input="reportFromMenu = false"></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="3" lg="2">
            <v-menu v-model="reportToMenu" offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  class="filter-select"
                  :value="formatDateTo"
                  label="Do datuma"
                  dense outlined hide-details
                  prepend-inner-icon="mdi-calendar"
                  readonly v-bind="attrs" v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="dateTo" no-title scrollable @input="reportToMenu = false"></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg="3">
            <v-autocomplete
              class="filter-select"
              dense outlined hide-details
              label="Prodajno mjesto"
              v-model="location"
              :items="locations"
              item-text="name"
              item-value="id"
              return-object
            ></v-autocomplete>
          </v-col>

          <!-- Action Cluster -->
          <v-col cols="auto" class="ml-auto d-flex align-center" style="gap: 8px">
            <v-btn :disabled="locationNotSelected" class="search-btn" @click="getData()" small>
              <v-icon small class="mr-1">mdi-magnify</v-icon>
              Pretraži
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-menu offset-y left :close-on-content-click="true">
                  <template v-slot:activator="{ on: menu, attrs }">
                    <v-btn icon class="export-btn" v-bind="attrs" v-on="{ ...tooltip, ...menu }">
                      <v-icon color="#00b3be">mdi-export-variant</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense style="border-radius: 12px; overflow: hidden; padding: 4px; min-width: 140px;">
                    <v-list-item @click="changeWidth()">
                      <v-icon size="18" color="#E53935" class="mr-2">mdi-file-pdf-box</v-icon>
                      <v-list-item-title>PDF</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="generateExcel()">
                      <v-icon size="18" color="#43A047" class="mr-2">mdi-file-excel</v-icon>
                      <v-list-item-title>Excel</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
              <span>Izvoz podataka</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon :to="to" class="close-btn" v-bind="attrs" v-on="on">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Zatvori izvještaj</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </div>

      <!-- Glass Table Wrapper -->
      <div class="glass-table-wrapper">
        <div id="table_info" class="table-content">
          <!-- Hidden company info for PDF -->
          <table id="firma" style="display: none; font-family: 'Roboto-Regular'">
            <tbody>
              <tr><td style="padding: 2px 4px;">{{ company ? $options.filters.capitalize(company.name) : '' }}</td></tr>
              <tr><td style="padding: 2px 4px;">{{ company ? company.address + ', ' + company.city : '' }}</td></tr>
              <tr><td style="padding: 2px 4px;">OIB: {{ company ? company.oib : '' }}</td></tr>
              <tr><td style="padding: 2px 4px;">Prodajno mjesto: {{ location ? location.name : '' }}</td></tr>
              <tr><td style="padding: 2px 4px;">Adresa prodajnog mjesta: {{ location ? location.address + ', ' + location.city : '' }}</td></tr>
            </tbody>
          </table>

          <!-- Report Title -->
          <div style="font-family: Raleway, sans-serif;">
            <table justify="center" style="width: 100% !important; border: 0 !important" cellspacing="0" id="title">
              <thead>
                <tr style="border: 0 !important">
                  <th colspan="8">
                    <p class="gradient-text" style="font-size: 25px; text-align: center;">Nivelacija</p>
                  </th>
                </tr>
                <tr>
                  <th colspan="8">
                    <p style="font-weight: bold; font-size: 12px; text-align: center;">
                      za razdoblje od {{ formatDateFrom }} do {{ formatDateTo }}
                    </p>
                  </th>
                </tr>
              </thead>
            </table>
          </div>

          <!-- Nivelacija Table -->
          <div v-if="showTableData" style="font-family: Raleway, sans-serif;">
            <table
              justify="center"
              style="width: 100% !important"
              cellspacing="0"
              id="tableTraffic"
            >
              <thead>
                <tr class="header naslov" style="text-align: left">
                  <th>Datum</th>
                  <th>Šifra</th>
                  <th>Naziv</th>
                  <th style="text-align: right">Stara MPC</th>
                  <th style="text-align: right">Nova MPC</th>
                  <th style="text-align: right">Razlika</th>
                  <th style="text-align: right">Količina</th>
                  <th style="text-align: right">Iznos</th>
                  <th>Opis</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in items" :key="idx">
                  <td>{{ formatDatum(item.datum) }}</td>
                  <td>{{ item.code }}</td>
                  <td>{{ item.name }}</td>
                  <td style="text-align: right">{{ viewType === 'pdf' ? $options.filters.money(item.old_price, 100, currency).replace('€', '') : (item.old_price / 100) }}</td>
                  <td style="text-align: right">{{ viewType === 'pdf' ? $options.filters.money(item.new_price, 100, currency).replace('€', '') : (item.new_price / 100) }}</td>
                  <td style="text-align: right">{{ viewType === 'pdf' ? $options.filters.money(item.razlika, 100, currency).replace('€', '') : (item.razlika / 100) }}</td>
                  <td style="text-align: right">{{ (item.kolicina / 1000).toFixed(3) }}</td>
                  <td style="text-align: right">{{ viewType === 'pdf' ? $options.filters.money(item.iznos, 100, currency).replace('€', '') : (item.iznos / 100) }}</td>
                  <td>{{ item.opis }}</td>
                </tr>
              </tbody>
              <tfoot v-if="items.length > 0">
                <tr class="row-total">
                  <th colspan="7" style="text-align: left">Ukupni iznos</th>
                  <th style="text-align: right">{{ viewType === 'pdf' ? $options.filters.money(totalIznos, 100, currency).replace('€', '') : (totalIznos / 100) }}</th>
                  <th></th>
                </tr>
              </tfoot>
            </table>
          </div>

          <div v-if="showTableData && items.length === 0" class="text-center py-8 grey--text">
            Nema podataka za odabrani period i lokaciju.
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import { DAY, clone } from '@/plugins/utils'
import { df, auth } from '@/plugins/firebase'
import state from '@/state'
import 'jspdf-autotable'
import { saveExcel } from '@/modules/all-reports/xlsx/config.js'
import EventBus from '@/plugins/event-bus'
import { initPdfLandscape, setTable, setPageNumber } from '@/modules/all-reports/pdf/config.js'

export default {
  name: 'Nivelacija',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],
  data () {
    return {
      dateFrom: new Date(new Date().getTime() - DAY).toISOString().substr(0, 10),
      dateTo: new Date().toISOString().substr(0, 10),
      reportFromMenu: undefined,
      reportToMenu: undefined,
      locations: [],
      location: undefined,
      company: undefined,
      items: [],
      showTableData: false,
      targetModifier: '',
      viewType: 'pdf',
      tableWidth: '1200px',
      a4PortraitWidth: '800px',
      a4LandscapeWidth: '1050px',
      currency: 'EUR',
      margin: '0px',
      listeners: []
    }
  },
  computed: {
    to () {
      return { name: `${this.targetModifier}.all-reports.reportMenu` }
    },
    formatDateFrom () {
      return new Intl.DateTimeFormat('hr-HR', { month: '2-digit', day: '2-digit', year: 'numeric' })
        .format(new Date(this.dateFrom))
    },
    formatDateTo () {
      return new Intl.DateTimeFormat('hr-HR', { month: '2-digit', day: '2-digit', year: 'numeric' })
        .format(new Date(this.dateTo))
    },
    locationNotSelected () {
      return !this.location || !this.location.id
    },
    totalIznos () {
      return this.items.reduce((sum, i) => sum + (i.iznos || 0), 0)
    }
  },
  async created () {
    this.margin = state.getMargin()
    await this.getPointsOfSale()
  },
  mounted () {
    const sideNav = this.$route.meta.sideNav
    switch (sideNav) {
      case 'Company':
        this.targetModifier = 'company'
        break
      case 'PointOfSale':
      case 'CashRegister':
        this.targetModifier = 'pointOfSale'
        break
    }

    EventBus.$on('set-mini', () => { this.margin = state.getMargin() })

    this.company = clone(state.getCurrentCompany())
    this.location = clone(state.getPointOfSale())
  },
  beforeDestroy () {
    EventBus.$off('set-mini')
    this.listeners.forEach(l => l())
  },
  methods: {
    formatDatum (timestamp) {
      if (!timestamp) return ''
      return new Intl.DateTimeFormat('hr-HR', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      }).format(new Date(timestamp * 1000))
    },
    async getPointsOfSale () {
      const docRef = df.doc(`user_locations/${auth.currentUser.uid}.${state.getCurrentCompany().id}`)
      const snap = await docRef.get()
      if (snap.exists && snap.data().locations) {
        this.locations = Object.values(snap.data().locations)
          .filter(l => l.resource_status === 'ACTIVE')
          .sort((a, b) => a.name.localeCompare(b.name))
      }
    },
    async getData () {
      if (this.locationNotSelected) return

      this.showLoader()
      try {
        const dateFrom = new Date(this.dateFrom).getTime() / 1000
        const dateTo = new Date(this.dateTo)
        dateTo.setHours(23, 59, 59, 999)
        const dateToTs = dateTo.getTime() / 1000

        const snap = await df
          .collection('nivelacija')
          .where('location_id', '==', this.location.id)
          .where('company_id', '==', state.getCurrentCompany().id)
          .get()

        this.items = snap.docs
          .map(d => d.data())
          .filter(d => d.datum >= dateFrom && d.datum <= dateToTs)
          .sort((a, b) => a.datum - b.datum)
        this.showTableData = true
      } catch (e) {
        this.showMsgBox({
          text: `Greška pri dohvatu podataka: ${e.message}`,
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      } finally {
        this.hideLoader()
      }
    },
    changeWidth () {
      this.showLoader('', { opacity: 1 })
      const tableInfo = document.getElementById('table_info')
      tableInfo.style.width = this.a4LandscapeWidth
      tableInfo.style.fontSize = '65%'
      this.generatePdf()
    },
    async generatePdf () {
      const tableInfo = document.getElementById('table_info')
      try {
        var doc = await initPdfLandscape()
        doc = await setTable(doc, '#firma', 10)
        var finalY = doc.lastAutoTable.finalY || 10
        doc = await setTable(doc, '#title', finalY)
        finalY = doc.lastAutoTable.finalY || 10
        doc = await setTable(doc, '#tableTraffic', finalY)
        doc = await setPageNumber(doc)
        doc.save('Nivelacija.pdf')
      } catch (error) {
        console.error('PDF generation error:', error)
      } finally {
        tableInfo.style.width = this.tableWidth
        tableInfo.style.fontSize = '100%'
        this.viewType = 'pdf'
        this.hideLoader()
      }
    },
    generateExcel () {
      this.showLoader('', { opacity: 1 })
      this.viewType = 'excel'
      setTimeout(() => {
        const tables = ['firma', 'title', 'tableTraffic']
        saveExcel(tables, 'Nivelacija.xlsx')
        this.viewType = 'pdf'
        this.hideLoader()
      }, 1000)
    }
  }
}
</script>

<style scoped>
.report-wrapper {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
}

.table-header-filters {
  position: -webkit-sticky;
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #ffffff;
  padding: 16px;
  margin-bottom: 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
}

.filter-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}
.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}
.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}
.filter-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  min-height: 36px !important;
  border-radius: 8px !important;
}
.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
}
.filter-select >>> label {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: rgba(57, 137, 201, 0.8) !important;
}

.close-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}
.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}

.search-btn {
  height: 36px !important;
  padding: 0 14px !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
}
.search-btn:hover:not(:disabled) {
  border-color: #3989C9 !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.2) !important;
}
.search-btn:disabled { opacity: 0.45 !important; }

.export-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(0, 179, 190, 0.06) !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  transition: all 0.2s ease !important;
}
.export-btn:hover {
  background: rgba(0, 179, 190, 0.12) !important;
  box-shadow: 0 0 0 3px rgba(0, 179, 190, 0.12) !important;
}

.glass-table-wrapper {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 30px;
  overflow-x: auto;
  margin-top: 16px;
}

.table-content {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  font-size: 16px;
  font-family: 'Roboto-Regular', sans-serif;
}

.gradient-text {
  font-weight: 700 !important;
  color: #2d3748 !important;
  -webkit-text-fill-color: #2d3748 !important;
  letter-spacing: 0.3px !important;
  display: inline-block !important;
  margin: 0;
  padding: 0;
}

.glass-table-wrapper table {
  border-collapse: collapse;
  width: 100%;
  border: none;
}
.glass-table-wrapper table th,
.glass-table-wrapper table td {
  padding: 14px 20px;
  border: none;
  border-bottom: 1px solid #EDF2F7;
  font-variant-numeric: tabular-nums;
}
.glass-table-wrapper table tbody tr:hover:not(.naslov):not(.row-total) {
  background: #F7FAFC;
}

.header.naslov {
  background: #F8FAFC !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
  color: #2D3748 !important;
}
.header.naslov th {
  color: #2D3748 !important;
  border-bottom: 2px solid #EDF2F7 !important;
}

.row-total {
  background: rgba(0, 188, 212, 0.03) !important;
  border-top: 2px solid rgba(0, 188, 212, 0.2) !important;
}
.row-total th, .row-total td {
  color: #00b3be !important;
  font-weight: 700 !important;
  border-bottom: 1px solid rgba(0, 188, 212, 0.15) !important;
}

@media (max-width: 960px) {
  .glass-table-wrapper { padding: 16px; }
  .table-content { font-size: 14px; }
}
@media (max-width: 600px) {
  .table-header-filters { padding: 12px; }
  .glass-table-wrapper { padding: 12px; }
  .table-content { font-size: 13px; }
}
</style>
